import type { Experiment } from "../types.js";

/**
 * REAL CONTENT — un experimento por cada uno de los nueve tipos.
 *
 * Los seis últimos (2, 3, 4, 5, 7, 8) se añadieron el 17 de agosto, al enchufar
 * el banco de nueve: sin ellos, seis de cada nueve personas recibían su
 * resultado y después la frase «no experiment is offered — the hypothesis isn't
 * clear enough yet», que habría sido FALSA. La hipótesis estaba clara; lo que
 * faltaba era esto.
 *
 * Fuente: `docs/canonical/Plantilla_maestra_nueve_eneatipos.docx`, **Bloque F**
 * («Aplicación del marco») y **Bloque H** («Riesgos de mala aplicación») de cada
 * tipo, más la §5 de `Especificacion_canonica_v1.1` para el aviso de seguridad.
 *
 * ESTO NO ES SCORING. El `DEFINITION_OF_DONE` reserva al autor los pesos de
 * clasificación; un experimento no puntúa nada — es lo que la persona hace
 * DESPUÉS de ver el resultado. Por eso se puede portar como se portaron los
 * patrones, mientras las situaciones siguen esperando.
 *
 * EL MAPEO, campo a campo:
 *
 *   Bloque F · «Notar»                    → `trigger`
 *   Bloque F · «Abrir margen, vía preparada» → `action`  (es un plan si-entonces,
 *                                             o sea ya una acción ejecutable)
 *   Bloque F · «Revisar»                  → `observation`
 *   Bloque H · «Microacción contraindicada» + «Límite ético específico»
 *                                         → `contraindications`
 *   §5 de la v1.1                         → `safetyCopy`, literal
 *
 * `timeWindow` son 48-72 h porque es la ventana del retorno que ya prevén los
 * eventos `followup_opt_in` / `followup_returned`. No sale del canon: es la
 * decisión de producto de `docs/PLAN-inner-map.md` §4.
 *
 * LO QUE MÁS IMPORTA Y ES FÁCIL DE PERDER: las contraindicaciones **no son
 * genéricas**. El canon identifica, POR TIPO, qué familia de microacción
 * refuerza el patrón en vez de abrirlo — y para el 9 y el 6 son justo las que
 * parecerían más naturales. Proponerle a un 9 que se tome un tiempo es darle
 * más de lo que ya hace. Ese detalle es el que separa esto de un consejo.
 *
 * En inglés, con el original español al lado, igual que los patrones.
 */

/** §5 de la v1.1, literal. La decisión canónica obliga a conservar cinco
 *  elementos: peligro, daño activo, coerción, irreversibilidad y apoyo. El
 *  fraseo puede mejorarse; esos cinco no se tocan. */
const SAFETY =
  "Don't run this experiment in situations involving danger, active harm, " +
  "coercion, or consequences that are hard to reverse. In those cases, put " +
  "safety first and find proper support.";

export const EXPERIMENTS: Experiment[] = [
  {
    id: "EXP-1",
    patternId: "1",
    // «Observar la tensión o irritación previa a verbalizar la crítica»
    trigger:
      "The moment you feel the tension or irritation that comes just before " +
      "you voice a criticism — yours or someone else's.",
    // «Si reviso lo mismo por tercera vez, entonces marco un límite y lo dejo así»
    action:
      "If you catch yourself going over the same thing a third time, stop " +
      "there: call that the limit and leave it as it is.",
    // «Distinguir el hecho de la interpretación ("esto significa que fallé")»
    observation:
      "Afterwards, separate what actually happened from what you made it mean " +
      "— “this means I failed” is the interpretation, not the fact.",
    timeWindow: "48–72 hours",
    contraindications: [
      // Bloque H del 1. La distinción importa: el riesgo aquí es de INTENSIDAD,
      // no de familia, así que no hay una microacción prohibida — hay una forma
      // de hacerlas mal.
      "No family of microaction is systematically contraindicated for this " +
        "pattern; the risk is intensity, not kind.",
      "Watch for the method itself becoming a new standard to meet.",
      "Ethical limit: don't use this to stop correcting errors that genuinely " +
        "need correcting."
    ],
    safetyCopy: SAFETY,
    sourceRefs: [
      "Plantilla_maestra_nueve_eneatipos.docx, Eneatipo 1, Bloque F y Bloque H",
      "Especificacion_canonica_v1.1 §5 (aviso de seguridad)"
    ],
    version: "canon-nueve-1.0"
  },

  {
    id: "EXP-6",
    patternId: "6",
    // «Observar la duda o la ansiedad anticipatoria en el momento en que
    //  aparece, antes de empezar a consultar o posponer»
    trigger:
      "The moment the doubt or the anticipatory worry shows up — before you " +
      "start asking around or putting it off.",
    // «Si me sorprendo consultando a la tercera persona sobre lo mismo,
    //  entonces decido con la información que ya tengo»
    action:
      "If you catch yourself asking a third person about the same thing, " +
      "decide with the information you already have.",
    // «Distinguir el hecho (qué pasó) de la interpretación ("esto confirma que
    //  no puedo confiar en mi criterio")»
    observation:
      "Afterwards, separate what happened from what you made it mean — “this " +
      "proves I can't trust my own judgement” is the interpretation.",
    timeWindow: "48–72 hours",
    contraindications: [
      // Bloque H del 6, y es el detalle que salva el experimento: para este
      // patrón, pedir otra opinión —lo más natural del mundo— lo refuerza.
      "Contraindicated: “get more information”. Asking for one more opinion " +
        "reinforces the pattern when it replaces the decision instead of " +
        "bounding it.",
      "Watch for the pause becoming a place to generate more risk scenarios " +
        "rather than to decide.",
      "“Just trust yourself more”, with no concrete instruction, becomes one " +
        "more thing to doubt.",
      "Ethical limit: don't use this to talk down a real risk. Intense worry " +
        "sometimes points at something that genuinely matters."
    ],
    safetyCopy: SAFETY,
    sourceRefs: [
      "Plantilla_maestra_nueve_eneatipos.docx, Eneatipo 6, Bloque F y Bloque H",
      "Especificacion_canonica_v1.1 §5 (aviso de seguridad)",
      "[LÍMITE] Vilaseca-céntrico: sin triangular, a diferencia de 1 y 9"
    ],
    version: "canon-nueve-1.0"
  },

  {
    id: "EXP-9",
    patternId: "9",
    // «Observar el instante en que se empieza a decir "sí" antes de haber
    //  decidido internamente qué se piensa»
    trigger:
      "The instant you start saying yes before you've decided, inside, what " +
      "you actually think.",
    // «Si me piden una opinión rápida, digo una preferencia concreta aunque
    //  sea pequeña»
    action:
      "If someone asks you for a quick opinion, name one concrete preference " +
      "— however small.",
    // «Distinguir el hecho de la interpretación ("daba igual, no importaba mi
    //  opinión")»
    observation:
      "Afterwards, separate what happened from what you made it mean — “it " +
      "didn't matter anyway” is the interpretation.",
    timeWindow: "48–72 hours",
    contraindications: [
      // Bloque H del 9. Igual que el 6, la microacción prohibida es la que
      // parecería más amable: darle tiempo a alguien que ya usa el tiempo para
      // no decidir es darle más patrón.
      "Contraindicated: “take some time”. Pausing or postponing feeds this " +
        "pattern directly rather than helping.",
      "Watch for “opening space” looking, from the outside, exactly like the " +
        "postponing this pattern already does.",
      "Ethical limit: don't use this to justify carrying on postponing under " +
        "the appearance of “still reviewing”."
    ],
    safetyCopy: SAFETY,
    sourceRefs: [
      "Plantilla_maestra_nueve_eneatipos.docx, Eneatipo 9, Bloque F y Bloque H",
      "Especificacion_canonica_v1.1 §5 (aviso de seguridad)"
    ],
    version: "canon-nueve-1.0"
  },
  {
    id: "EXP-2",
    patternId: "2",
    // Observar el impulso de ofrecer ayuda o un halago antes de haber registrado la propia necesidad
    trigger:
      "The moment you feel the pull to offer help — or a compliment — before you have registered what you need yourself.",
    // «Si me piden ayuda, primero reviso mi disponibilidad antes de responder»
    action:
      "If someone asks you for help, check what you actually have to give before you answer.",
    // Distinguir el hecho de la interpretación («si no ayudo, no me van a querer»)
    observation:
      "Afterwards, separate what happened from what you made it mean — \u201cif I don\u2019t help, they won\u2019t want me around\u201d is the interpretation.",
    timeWindow: "48\u201372 hours",
    contraindications: [
      "Contraindicated: delegating as the microaction. Handing a piece to someone else easily becomes another way of showing generosity, which puts the pattern straight back.",
      "Watch for checking your availability turning into a formality that changes nothing.",
      "Ethical limit: don't use this to justify withholding help where it is genuinely needed and nobody else can give it."
    ],
    safetyCopy: SAFETY,
    sourceRefs: [
      "Plantilla_maestra_nueve_eneatipos.docx, Eneatipo 2, Bloque F y Bloque H",
      "Especificacion_canonica_v1.1 \u00a75 (aviso de seguridad)"
    ],
    version: "canon-nueve-1.0"
  },
  {
    id: "EXP-3",
    patternId: "3",
    // Observar el impulso de acelerar o proyectar imagen cuando aparece la posibilidad de un resultado mediocre
    trigger:
      "The moment you feel yourself speeding up, or managing how this looks, because the result might turn out ordinary.",
    // «Si noto que estoy acelerando para evitar un sentimiento, entonces me detengo 2 minutos antes de seguir»
    action:
      "If you notice you are speeding up to get away from a feeling, stop for two minutes before carrying on.",
    // Distinguir el hecho (qué se logró o no) de la interpretación («esto me hace un fracaso»)
    observation:
      "Afterwards, separate what you did or didn't achieve from what you made it mean — \u201cthis makes me a failure\u201d is the interpretation.",
    timeWindow: "48\u201372 hours",
    contraindications: [
      "Contraindicated: sharing your wins as the microaction. Done for validation rather than contact, it reinforces the pattern instead of opening it.",
      "Watch for the pause becoming one more thing to have done well.",
      "Ethical limit: don't use this to perform vulnerability as a better image."
    ],
    safetyCopy: SAFETY,
    sourceRefs: [
      "Plantilla_maestra_nueve_eneatipos.docx, Eneatipo 3, Bloque F y Bloque H",
      "Especificacion_canonica_v1.1 \u00a75 (aviso de seguridad)"
    ],
    version: "canon-nueve-1.0"
  },
  {
    id: "EXP-4",
    patternId: "4",
    // Observar la melancolía o la comparación en el momento en que aparece, antes de que se convierta en narrativa de carencia
    trigger:
      "The moment the melancholy or the comparison shows up — before it turns into a story about what you lack.",
    // «Si me sorprendo comparándome, entonces nombro una cosa concreta que valoro de mi situación actual»
    action:
      "If you catch yourself comparing, name one concrete thing you value about where you actually are.",
    // Distinguir el hecho de la interpretación («esto confirma que soy incomprendido/a»)
    observation:
      "Afterwards, separate what happened from what you made it mean — \u201cthis proves nobody understands me\u201d is the interpretation.",
    timeWindow: "48\u201372 hours",
    contraindications: [
      "Contraindicated: making something about it, for yourself alone. Creative work with no link to anyone else deepens the withdrawal rather than opening it.",
      "Watch for the microaction feeling like a betrayal of who you are. That feeling is part of the pattern, not an argument against the experiment.",
      "Ethical limit: don't use this to pathologise sensitivity, or to force an optimism that isn't warranted."
    ],
    safetyCopy: SAFETY,
    sourceRefs: [
      "Plantilla_maestra_nueve_eneatipos.docx, Eneatipo 4, Bloque F y Bloque H",
      "Especificacion_canonica_v1.1 \u00a75 (aviso de seguridad)"
    ],
    version: "canon-nueve-1.0"
  },
  {
    id: "EXP-5",
    patternId: "5",
    // Observar el impulso de retirarse o desconectar ante una demanda social o emocional
    trigger:
      "The moment you feel the pull to withdraw or switch off, when something social or emotional is being asked of you.",
    // «Si me piden algo que me hace querer retirarme, entonces doy una respuesta breve antes de desconectar del todo»
    action:
      "If you are asked for something that makes you want to withdraw, give a short answer before you disconnect entirely.",
    // Distinguir el hecho (qué se pidió realmente) de la interpretación («esto me va a vaciar»)
    observation:
      "Afterwards, separate what was actually asked from what you made it mean — \u201cthis is going to drain me\u201d is the interpretation.",
    timeWindow: "48\u201372 hours",
    contraindications: [
      "Contraindicated: getting more information first. Researching reinforces this pattern whenever it replaces the action instead of preparing it.",
      "Watch for the short answer becoming its own way of leaving the room.",
      "Ethical limit: don't use this to force an emotional exposure the person cannot safely hold."
    ],
    safetyCopy: SAFETY,
    sourceRefs: [
      "Plantilla_maestra_nueve_eneatipos.docx, Eneatipo 5, Bloque F y Bloque H",
      "Especificacion_canonica_v1.1 \u00a75 (aviso de seguridad)"
    ],
    version: "canon-nueve-1.0"
  },
  {
    id: "EXP-7",
    patternId: "7",
    // Observar el impulso de planificar o cambiar de tema en cuanto aparece un malestar
    trigger:
      "The moment you feel the pull to plan something else, or change the subject, as soon as discomfort turns up.",
    // «Si noto que quiero cambiar de tema o de plan, entonces me quedo un minuto más antes de decidir»
    action:
      "If you notice you want to change the subject or the plan, stay one minute longer before deciding.",
    // Distinguir el hecho (qué malestar apareció) de la interpretación («esto es insoportable, necesito otra cosa»)
    observation:
      "Afterwards, separate what discomfort actually turned up from what you made it mean — \u201cthis is unbearable, I need something else\u201d is the interpretation.",
    timeWindow: "48\u201372 hours",
    contraindications: [
      "Contraindicated: changing the plan as the microaction. Used systematically it becomes the escape itself rather than a way out of it.",
      "Watch for the minute becoming a target to hit, emptied of the thing it was for.",
      "Ethical limit: don't use this to keep someone inside a situation that is genuinely harmful, under the label of tolerating discomfort."
    ],
    safetyCopy: SAFETY,
    sourceRefs: [
      "Plantilla_maestra_nueve_eneatipos.docx, Eneatipo 7, Bloque F y Bloque H",
      "Especificacion_canonica_v1.1 \u00a75 (aviso de seguridad)"
    ],
    version: "canon-nueve-1.0"
  },
  {
    id: "EXP-8",
    patternId: "8",
    // Observar la activación o tensión corporal en el momento en que aparece la sensación de estar siendo controlado/a
    trigger:
      "The moment your body tightens with the sense that someone is trying to control you.",
    // «Si siento que me quieren controlar, entonces cuento hasta diez antes de responder»
    action:
      "If you feel someone is trying to control you, count to ten before you answer.",
    // Distinguir el hecho (qué se dijo o hizo) de la interpretación («esto es un intento de dominarme»)
    observation:
      "Afterwards, separate what was actually said or done from what you made it mean — \u201cthat was an attempt to dominate me\u201d is the interpretation.",
    timeWindow: "48\u201372 hours",
    contraindications: [
      "Contraindicated: lowering your tone as the microaction. Done as another way of managing the situation rather than as genuine opening, it reinforces the pattern.",
      "Watch for the pause being experienced as weakness and dropped the moment there is pressure.",
      "Ethical limit: don't use this to demand vulnerability on someone else's schedule. The pace of opening is theirs to set."
    ],
    safetyCopy: SAFETY,
    sourceRefs: [
      "Plantilla_maestra_nueve_eneatipos.docx, Eneatipo 8, Bloque F y Bloque H",
      "Especificacion_canonica_v1.1 \u00a75 (aviso de seguridad)"
    ],
    version: "canon-nueve-1.0"
  }
];
